// /client/src/components/Contacts.js
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navigation from './Navigation';

// Default confirmation messages for opt-in
const DEFAULT_OPT_IN_MESSAGE = (firstName, companyName = 'Next Move') => `Hello${firstName ? ` ${firstName}` : ''}. This is an automated message from the team at ${companyName}. To receive daily prompts and messages from our program, reply JOIN. We recommend adding ${companyName} to your contacts before typing JOIN.\n\nYou can opt out anytime by replying STOP. Message and data rates may apply. Learn more about our privacy practices at nextmovenetwork.com.`;
const DEFAULT_OPT_IN_CONFIRMATION = (companyName = 'Next Move') => `You're now subscribed to receiving messages from ${companyName}. Message and data rates may apply. Reply STOP to opt out anytime.`;
const DEFAULT_OPT_OUT_CONFIRMATION = (companyName = 'Next Move') => `You have successfully unsubscribed and will no longer receive messages from ${companyName}. To rejoin at any time, reply START. Message and data rates may apply.`;

const Contacts = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // State management
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [companyName, setCompanyName] = useState('Next Move');

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('active');
  
  // Checkbox selection state
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [showPodModal, setShowPodModal] = useState(false);
  const [podName, setPodName] = useState('');
  const [podId, setPodId] = useState('');
  const [creatingPod, setCreatingPod] = useState(false);
  
  // Status change modal state
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  // Bulk Opt-In modal state
  const [showBulkOptInModal, setShowBulkOptInModal] = useState(false);
  const [bulkOptInMessage, setBulkOptInMessage] = useState('');
  const [bulkOptInConfirmation, setBulkOptInConfirmation] = useState(DEFAULT_OPT_IN_CONFIRMATION);
  const [bulkOptOutConfirmation, setBulkOptOutConfirmation] = useState(DEFAULT_OPT_OUT_CONFIRMATION);
  const [sendingBulkOptIn, setSendingBulkOptIn] = useState(false);
  const [showAdvancedOptInOptions, setShowAdvancedOptInOptions] = useState(false);
  const [bulkOptInResults, setBulkOptInResults] = useState(null);
  
  // CSV Upload state
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [fieldMapping, setFieldMapping] = useState({});
  const [csvStep, setCsvStep] = useState(1); // 1: Upload, 2: Map Fields, 3: Preview & Confirm
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [csvErrors, setCsvErrors] = useState([]);
  const [csvImportRole, setCsvImportRole] = useState('participant'); // Default role for CSV imports
  
  // Contact fields available for mapping - now role-aware
  const getContactFields = (role) => {
    if (role === 'participant') {
      return [
        { key: 'fullName', label: 'Full Name', required: true },
        { key: 'phone', label: 'Phone', required: true },
        { key: 'email', label: 'Email', required: false },
        { key: 'status', label: 'Status', required: false },
      ];
    } else {
      // Admin or Host
      return [
        { key: 'fullName', label: 'Full Name', required: true },
        { key: 'email', label: 'Email', required: true },
        { key: 'phone', label: 'Phone', required: false },
        { key: 'status', label: 'Status', required: false },
      ];
    }
  };
  
  // Get current contact fields based on selected import role
  const contactFields = useMemo(() => getContactFields(csvImportRole), [csvImportRole]);
  
  // State for sorting
  const [sortConfig, setSortConfig] = useState({
    key: 'createdAt',
    direction: 'descending'
  });

  // Fetch contacts from API
  const fetchContacts = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('No authentication token found');
        return;
      }
      
      const response = await fetch('/api/contacts', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setContacts(data.contacts || []);
      } else {
        const errorData = await response.json();
        console.error('API error:', errorData);
        setError(errorData.error || 'Failed to fetch contacts');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  // Fetch contacts on component mount
  useEffect(() => {
    fetchContacts();
  }, []);

  // Check for success message from location state
  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // Fetch company name
  useEffect(() => {
    const fetchCompanyName = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/company', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          
          if (data.company?.name) {
            setCompanyName(data.company.name);
            
          }
        } else {
          
        }
      } catch (error) {
        console.error('Failed to fetch company name:', error);
      }
    };
    fetchCompanyName();
  }, []);

  // CSV Parsing function
  const parseCSV = (text) => {
    const lines = text.split(/\r\n|\n/);
    const headers = [];
    const data = [];
    
    if (lines.length === 0) return { headers: [], data: [] };
    
    // Parse headers (first line)
    const headerLine = lines[0];
    let currentField = '';
    let inQuotes = false;
    
    for (let i = 0; i < headerLine.length; i++) {
      const char = headerLine[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        headers.push(currentField.trim());
        currentField = '';
      } else {
        currentField += char;
      }
    }
    headers.push(currentField.trim());
    
    // Parse data rows
    for (let lineIndex = 1; lineIndex < lines.length; lineIndex++) {
      const line = lines[lineIndex];
      if (!line.trim()) continue; // Skip empty lines
      
      const row = {};
      let fieldIndex = 0;
      currentField = '';
      inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          if (fieldIndex < headers.length) {
            row[headers[fieldIndex]] = currentField.trim();
          }
          currentField = '';
          fieldIndex++;
        } else {
          currentField += char;
        }
      }
      // Don't forget the last field
      if (fieldIndex < headers.length) {
        row[headers[fieldIndex]] = currentField.trim();
      }
      
      data.push(row);
    }
    
    return { headers, data };
  };

  // Handle CSV file selection
  const handleCsvFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      setCsvErrors(['Please select a CSV file']);
      return;
    }
    
    setCsvFile(file);
    setCsvErrors([]);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const { headers, data } = parseCSV(text);
      
      if (headers.length === 0 || data.length === 0) {
        setCsvErrors(['CSV file is empty or invalid']);
        return;
      }
      
      setCsvHeaders(headers);
      setCsvData(data);
      
      // Auto-map fields based on header names
      autoMapFields(headers);
      setCsvStep(2);
    };
    
    reader.onerror = () => {
      setCsvErrors(['Failed to read file']);
    };
    
    reader.readAsText(file);
  };

  // Auto-map fields based on CSV headers
  const autoMapFields = (headers) => {
    const autoMapping = {};
    const fields = getContactFields(csvImportRole);
    
    headers.forEach(header => {
      const headerLower = header.toLowerCase().replace(/[_\s-]/g, '');
      
      fields.forEach(field => {
        const fieldLower = field.key.toLowerCase();
        const fieldLabelLower = field.label.toLowerCase().replace(/[_\s-]/g, '');
        
        if (headerLower === fieldLower || 
            headerLower === fieldLabelLower ||
            headerLower.includes(fieldLower) ||
            fieldLower.includes(headerLower)) {
          if (!autoMapping[field.key]) {
            autoMapping[field.key] = header;
          }
        }
      });
      
      // Special cases
      if ((headerLower.includes('name') && !headerLower.includes('first') && !headerLower.includes('last')) ||
          headerLower === 'fullname' || headerLower === 'contactname') {
        if (!autoMapping.fullName) autoMapping.fullName = header;
      }
      if (headerLower.includes('email') || headerLower.includes('mail')) {
        if (!autoMapping.email) autoMapping.email = header;
      }
      if (headerLower.includes('phone') || headerLower.includes('mobile') || headerLower.includes('cell')) {
        if (!autoMapping.phone) autoMapping.phone = header;
      }
    });
    
    setFieldMapping(autoMapping);
  };

  // Re-run auto-mapping when role changes
  const handleImportRoleChange = (newRole) => {
    setCsvImportRole(newRole);
    if (csvHeaders.length > 0) {
      // Re-run auto-mapping with new role's fields
      const autoMapping = {};
      const fields = getContactFields(newRole);
      
      csvHeaders.forEach(header => {
        const headerLower = header.toLowerCase().replace(/[_\s-]/g, '');
        
        fields.forEach(field => {
          const fieldLower = field.key.toLowerCase();
          const fieldLabelLower = field.label.toLowerCase().replace(/[_\s-]/g, '');
          
          if (headerLower === fieldLower || 
              headerLower === fieldLabelLower ||
              headerLower.includes(fieldLower) ||
              fieldLower.includes(headerLower)) {
            if (!autoMapping[field.key]) {
              autoMapping[field.key] = header;
            }
          }
        });
        
        // Special cases
        if ((headerLower.includes('name') && !headerLower.includes('first') && !headerLower.includes('last')) ||
            headerLower === 'fullname' || headerLower === 'contactname') {
          if (!autoMapping.fullName) autoMapping.fullName = header;
        }
        if (headerLower.includes('email') || headerLower.includes('mail')) {
          if (!autoMapping.email) autoMapping.email = header;
        }
        if (headerLower.includes('phone') || headerLower.includes('mobile') || headerLower.includes('cell')) {
          if (!autoMapping.phone) autoMapping.phone = header;
        }
      });
      
      setFieldMapping(autoMapping);
    }
  };

  // Handle field mapping change
  const handleMappingChange = (contactField, csvHeader) => {
    setFieldMapping(prev => ({
      ...prev,
      [contactField]: csvHeader || undefined
    }));
  };

  // Get mapped preview data
  const getMappedPreviewData = useMemo(() => {
    return csvData.slice(0, 5).map((row, index) => {
      const mappedRow = { _rowIndex: index + 1 };
      contactFields.forEach(field => {
        const csvHeader = fieldMapping[field.key];
        mappedRow[field.key] = csvHeader ? row[csvHeader] : '';
      });
      return mappedRow;
    });
  }, [csvData, fieldMapping, contactFields]);

  // Validate mapping before proceeding
  const validateMapping = () => {
    const errors = [];
    const requiredFields = contactFields.filter(f => f.required);
    
    requiredFields.forEach(field => {
      if (!fieldMapping[field.key]) {
        errors.push(`${field.label} is required and must be mapped`);
      }
    });
    
    return errors;
  };

  // Proceed to preview step
  const handleProceedToPreview = () => {
    const errors = validateMapping();
    if (errors.length > 0) {
      setCsvErrors(errors);
      return;
    }
    setCsvErrors([]);
    setCsvStep(3);
  };

  // Process and upload CSV contacts
  const handleUploadCsv = async () => {
    const errors = validateMapping();
    if (errors.length > 0) {
      setCsvErrors(errors);
      return;
    }
    
    setUploadingCsv(true);
    setCsvErrors([]);
    
    try {
      const token = localStorage.getItem('token');
      
      // Transform CSV data to contacts format
      const contactsToCreate = csvData.map(row => {
        const contact = {
          role: csvImportRole // Set the role from the selector
        };
        
        contactFields.forEach(field => {
          const csvHeader = fieldMapping[field.key];
          if (csvHeader && row[csvHeader]) {
            let value = row[csvHeader].trim();
            
            // Normalize status values
            if (field.key === 'status') {
              value = value.toLowerCase();
              if (!['active', 'inactive', 'archived', 'prospecting'].includes(value)) {
                value = 'active'; // Default to active
              }
            }
            
            contact[field.key] = value;
          }
        });
        
        // Set defaults for non-mapped optional fields
        if (!contact.status) contact.status = 'active';
        
        return contact;
      });
      
      // Filter out invalid rows based on role requirements
      const validContacts = contactsToCreate.filter(contact => {
        if (csvImportRole === 'participant') {
          return contact.fullName && contact.phone;
        } else {
          return contact.fullName && contact.email;
        }
      });
      
      if (validContacts.length === 0) {
        const requiredFields = csvImportRole === 'participant' ? 'Name and Phone' : 'Name and Email';
        setCsvErrors([`No valid contacts found. Please ensure all rows have ${requiredFields}.`]);
        setUploadingCsv(false);
        return;
      }
      
      const response = await fetch('/api/contacts/bulk', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ contacts: validContacts })
      });
      
      if (response.ok) {
        const data = await response.json();
        const skipped = contactsToCreate.length - validContacts.length;
        let message = `Successfully created ${data.created || validContacts.length} contact${(data.created || validContacts.length) !== 1 ? 's' : ''}`;
        
        if (data.duplicates && data.duplicates > 0) {
          message += ` (${data.duplicates} duplicate${data.duplicates !== 1 ? 's' : ''} skipped)`;
        }
        if (skipped > 0) {
          message += ` (${skipped} invalid row${skipped !== 1 ? 's' : ''} skipped)`;
        }
        
        setSuccessMessage(message);
        resetCsvModal();
        fetchContacts();
      } else {
        const errorData = await response.json();
        setCsvErrors([errorData.error || 'Failed to upload contacts']);
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      setCsvErrors(['Failed to upload contacts. Please try again.']);
    } finally {
      setUploadingCsv(false);
    }
  };

  // Reset CSV modal state
  const resetCsvModal = () => {
    setShowCsvModal(false);
    setCsvFile(null);
    setCsvData([]);
    setCsvHeaders([]);
    setFieldMapping({});
    setCsvStep(1);
    setCsvErrors([]);
    setCsvImportRole('participant');
  };

  // Sorting functions
  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Filter and sort contacts
  const filteredAndSortedContacts = useMemo(() => {
    let filtered = [...contacts];
    
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(contact => {
        const fullName = contact.fullName?.toLowerCase() || '';
        const email = contact.email?.toLowerCase() || '';
        const phone = contact.phone?.toLowerCase() || '';
        
        return fullName.includes(searchLower) || 
               email.includes(searchLower) || 
               phone.includes(searchLower);
      });
    }
    
    // Apply role filter
    if (selectedRole) {
      filtered = filtered.filter(contact => contact.role === selectedRole);
    }
    
    // Apply status filter
    if (selectedStatus) {
      filtered = filtered.filter(contact => contact.status === selectedStatus);
    }
    
    // Apply sorting
    if (sortConfig !== null) {
      filtered.sort((a, b) => {
        let aValue, bValue;

        // For "contactInfo" sort, use phone first then fall back to email
        if (sortConfig.key === 'contactInfo') {
          aValue = (a.phone || a.email || '').toString().toLowerCase();
          bValue = (b.phone || b.email || '').toString().toLowerCase();
        } else if (sortConfig.key === 'createdAt' || sortConfig.key === 'lastLogin') {
          aValue = new Date(a[sortConfig.key] || 0);
          bValue = new Date(b[sortConfig.key] || 0);
        } else if (sortConfig.key === 'loginCount') {
          aValue = parseInt(a[sortConfig.key] || 0);
          bValue = parseInt(b[sortConfig.key] || 0);
        } else {
          aValue = (a[sortConfig.key] || '').toString().toLowerCase();
          bValue = (b[sortConfig.key] || '').toString().toLowerCase();
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return filtered;
  }, [contacts, searchTerm, selectedRole, selectedStatus, sortConfig]);

  // Check if contact is eligible to be added to a pod
  const isEligibleForPod = (contact) => {
    return contact.role === 'participant' && 
           contact.smsVerified === true && 
           !contact.activePod;
  };

  const getIneligibleReason = (contact) => {
    if (contact.role !== 'participant') {
      return 'Only participants can be added to pods';
    }
    if (!contact.smsVerified) {
      return 'Contact has not opted in to SMS';
    }
    if (contact.activePod) {
      return `Already in pod: ${contact.activePod.podName}`;
    }
    return '';
  };

  // Check if contact is eligible for opt-in request
  const isEligibleForOptIn = (contact) => {
    return contact.role === 'participant' && 
           contact.smsVerified !== true && 
           contact.phone;
  };

  // Get eligible contacts from current selection
  const eligibleSelectedContacts = useMemo(() => {
    return selectedContacts.filter(id => {
      const contact = filteredAndSortedContacts.find(c => (c.id || c._id) === id);
      return contact && isEligibleForPod(contact);
    });
  }, [selectedContacts, filteredAndSortedContacts]);

  // Get contacts eligible for opt-in from current selection
  const eligibleForOptInContacts = useMemo(() => {
    return selectedContacts.filter(id => {
      const contact = filteredAndSortedContacts.find(c => (c.id || c._id) === id);
      return contact && isEligibleForOptIn(contact);
    });
  }, [selectedContacts, filteredAndSortedContacts]);

  // Get full contact objects for opt-in eligible contacts
  const eligibleForOptInContactObjects = useMemo(() => {
    return eligibleForOptInContacts.map(id => {
      return filteredAndSortedContacts.find(c => (c.id || c._id) === id);
    }).filter(Boolean);
  }, [eligibleForOptInContacts, filteredAndSortedContacts]);

  // Get contacts that are already in an active pod from current selection
  const contactsAlreadyInPod = useMemo(() => {
    return selectedContacts.filter(id => {
      const contact = filteredAndSortedContacts.find(c => (c.id || c._id) === id);
      return contact && contact.activePod;
    });
  }, [selectedContacts, filteredAndSortedContacts]);

  // Get full contact objects for contacts already in pods (for display)
  const contactsAlreadyInPodObjects = useMemo(() => {
    return contactsAlreadyInPod.map(id => {
      return filteredAndSortedContacts.find(c => (c.id || c._id) === id);
    }).filter(Boolean);
  }, [contactsAlreadyInPod, filteredAndSortedContacts]);

  // Get all eligible contacts in filtered list
  const eligibleContacts = useMemo(() => {
    return filteredAndSortedContacts.filter(isEligibleForPod);
  }, [filteredAndSortedContacts]);

  // Checkbox handlers
  const handleSelectContact = (id) => {
    setSelectedContacts(prev => {
      if (prev.includes(id)) {
        return prev.filter(contactId => contactId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedContacts.length === filteredAndSortedContacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(filteredAndSortedContacts.map(c => c.id || c._id));
    }
  };

  const handleCreatePodClick = () => {
    // Check if any selected contacts are already in a pod
    if (contactsAlreadyInPod.length > 0) {
      const names = contactsAlreadyInPodObjects
        .map(c => `${c.fullName} (${c.activePod?.podName})`)
        .join(', ');
      setError(`Cannot create pod: The following contact(s) are already in an active pod: ${names}. Please deselect them first.`);
      return;
    }

    // Check if any eligible contacts are selected
    if (eligibleSelectedContacts.length === 0) {
      setError('No eligible contacts selected. Only participants who have opted in to SMS can be added to pods.');
      return;
    }

    setShowPodModal(true);
    
    // Generate Pod ID with current date/time in America/Los_Angeles timezone
    const now = new Date();
    const dateTimeString = now.toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    // Format as: POD-YYYYMMDD-HHMMSS
    const formattedDateTime = dateTimeString
      .replace(/(\d+)\/(\d+)\/(\d+),?\s+(\d+):(\d+):(\d+)/, 'POD-$3$1$2-$4$5$6');
    
    setPodId(formattedDateTime);
    setPodName('');
  };

  const handleCreatePod = async () => {
    if (!podName.trim() || !podId.trim()) {
      setError('Please provide both Pod ID and Pod Name');
      return;
    }

    if (eligibleSelectedContacts.length === 0) {
      setError('No eligible contacts selected');
      return;
    }

    try {
      setCreatingPod(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch('/api/pods', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          podId: podId.trim(),
          podName: podName.trim(),
          members: eligibleSelectedContacts.map(userId => ({
            userId,
            role: 'participant'
          }))
        })
      });

      if (response.ok) {
        const data = await response.json();
        const skippedCount = selectedContacts.length - eligibleSelectedContacts.length;
        let message = `Pod "${podName}" created successfully with ${eligibleSelectedContacts.length} member${eligibleSelectedContacts.length !== 1 ? 's' : ''}`;
        if (skippedCount > 0) {
          message += ` (${skippedCount} ineligible contact${skippedCount !== 1 ? 's' : ''} skipped)`;
        }
        setSuccessMessage(message);
        setShowPodModal(false);
        setSelectedContacts([]);
        setPodName('');
        setPodId('');
        fetchContacts();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to create pod');
      }
    } catch (error) {
      console.error('Error creating pod:', error);
      setError('Failed to create pod');
    } finally {
      setCreatingPod(false);
    }
  };

  // Status change handlers
  const handleChangeStatusClick = () => {
    setNewStatus('');
    setShowStatusModal(true);
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) {
      setError('Please select a status');
      return;
    }

    if (selectedContacts.length === 0) {
      setError('Please select at least one contact');
      return;
    }

    try {
      setUpdatingStatus(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch('/api/contacts/bulk/status', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contactIds: selectedContacts,
          status: newStatus
        })
      });

      if (response.ok) {
        const data = await response.json();
        const statusLabel = newStatus.charAt(0).toUpperCase() + newStatus.slice(1);
        setSuccessMessage(data.message || `Successfully updated ${selectedContacts.length} contact${selectedContacts.length !== 1 ? 's' : ''} to "${statusLabel}"`);
        setShowStatusModal(false);
        setSelectedContacts([]);
        setNewStatus('');
        fetchContacts();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update contact status');
      }
    } catch (error) {
      console.error('Error updating contact status:', error);
      setError('Failed to update contact status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Bulk Opt-In handlers
  const handleBulkOptInClick = () => {
    if (eligibleForOptInContacts.length === 0) {
      setError('No eligible contacts selected. Only participants without opt-in status can receive opt-in requests.');
      return;
    }

    setBulkOptInMessage(DEFAULT_OPT_IN_MESSAGE('', companyName));
    setBulkOptInConfirmation(DEFAULT_OPT_IN_CONFIRMATION(companyName));
    setBulkOptOutConfirmation(DEFAULT_OPT_OUT_CONFIRMATION(companyName));
    setShowAdvancedOptInOptions(false);
    setBulkOptInResults(null);
    setShowBulkOptInModal(true);
  };

  const handleSendBulkOptIn = async () => {
    if (eligibleForOptInContacts.length === 0) {
      setError('No eligible contacts to send opt-in requests');
      return;
    }

    if (!bulkOptInMessage.trim()) {
      setError('Please enter an opt-in message');
      return;
    }

    try {
      setSendingBulkOptIn(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch('/api/twilio/bulk-optin', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contactIds: eligibleForOptInContacts,
          customMessage: bulkOptInMessage.trim(),
          customOptInConfirmation: bulkOptInConfirmation.trim() || null,
          customOptOutConfirmation: bulkOptOutConfirmation.trim() || null
        })
      });

      const data = await response.json();

      if (response.ok) {
        setBulkOptInResults(data);
        
        let message = `Opt-in requests sent to ${data.sent || 0} contact${(data.sent || 0) !== 1 ? 's' : ''}`;
        if (data.failed > 0) {
          message += ` (${data.failed} failed)`;
        }
        if (data.skipped > 0) {
          message += ` (${data.skipped} skipped)`;
        }
        
        setSuccessMessage(message);
        
        setTimeout(() => {
          setShowBulkOptInModal(false);
          setSelectedContacts([]);
          fetchContacts();
        }, 2000);
      } else {
        setError(data.error || 'Failed to send opt-in requests');
      }
    } catch (error) {
      console.error('Error sending bulk opt-in:', error);
      setError('Failed to send opt-in requests. Please try again.');
    } finally {
      setSendingBulkOptIn(false);
    }
  };

  const resetBulkOptInModal = () => {
    setShowBulkOptInModal(false);
    setBulkOptInMessage('');
    setBulkOptInConfirmation(DEFAULT_OPT_IN_CONFIRMATION);
    setBulkOptOutConfirmation(DEFAULT_OPT_OUT_CONFIRMATION);
    setShowAdvancedOptInOptions(false);
    setBulkOptInResults(null);
  };

  const getSortIcon = (columnName) => {
    const iconStyle = {
      width: '12px',
      height: '12px',
      marginLeft: '8px',
      display: 'inline-block'
    };

    if (sortConfig.key !== columnName) {
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 5.83L15.17 9H13v6h-2V9H8.83L12 5.83zM12 18.17L8.83 15H11V9h2v6h2.17L12 18.17z"/>
        </svg>
      );
    }
    
    if (sortConfig.direction === 'ascending') {
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 6.83L15.17 10H13v8h-2v-8H8.83L12 6.83z"/>
        </svg>
      );
    } else {
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 17.17L8.83 14H11V6h2v8h2.17L12 17.17z"/>
        </svg>
      );
    }
  };

  const handleRowClick = (id, e) => {
    if (e.target.type === 'checkbox') {
      return;
    }
    navigate(`/contacts/${id}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleCreateContact = () => {
    navigate('/contacts/create');
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'is-danger';
      case 'host':
        return 'is-warning';
      case 'partcipant':
      default:
        return 'is-primary';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'is-success';
      case 'inactive':
        return 'is-light';
      case 'suspended':
        return 'is-warning';
      case 'deleted':
        return 'is-danger';
      case 'archived':
        return 'is-dark';
      case 'prospecting':
        return 'is-info';
      default:
        return 'is-light';
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedRole('');
    setSelectedStatus('');
  };

  if (loading) {
    return (
      <>
        <section className="theme-light m-0 p-0" data-theme="light">
          <div className='is-flex is-flex-direction-column is-justify-content-center'>
            <Navigation />
            <div className='mt-6 pt-6 container'>
              <div className="has-text-centered py-6">
                <div className="is-loading"></div>
                <p>Loading contacts...</p>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          
          <Navigation />

          <div className='mt-2 pt-6 container px-6'>
            <div className='pb-4 is-flex is-justify-content-space-between is-align-items-center'>
              <h1 className='h1 my-2'>Contacts</h1>
              <div className="buttons">
                {selectedContacts.length > 0 && (
                  <>
                    <button 
                      className="button is-dark"
                      onClick={handleChangeStatusClick}
                    >
                      Change Status{selectedContacts.length > 1 ? 'es' : ''} ({selectedContacts.length})
                    </button>
                    <button 
                      className="button is-info"
                      onClick={handleBulkOptInClick}
                      disabled={eligibleForOptInContacts.length === 0}
                      title={
                        eligibleForOptInContacts.length === 0
                          ? 'No contacts need opt-in (must be participants without SMS verification)'
                          : `Send opt-in requests to ${eligibleForOptInContacts.length} contact(s)`
                      }
                    >
                      Send Opt-In ({eligibleForOptInContacts.length})
                    </button>
                    <button 
                      className="button is-primary"
                      onClick={handleCreatePodClick}
                      disabled={selectedStatus === 'archived' || eligibleSelectedContacts.length === 0 || contactsAlreadyInPod.length > 0}
                      title={
                        contactsAlreadyInPod.length > 0
                          ? `${contactsAlreadyInPod.length} selected contact(s) already in a pod`
                          : selectedStatus === 'archived' 
                            ? 'Cannot create pods with archived contacts' 
                            : eligibleSelectedContacts.length === 0
                              ? 'No eligible contacts selected (must be participants who opted in)'
                              : ''
                      }
                    >
                      Create Pod ({eligibleSelectedContacts.length})
                    </button>
                  </>
                )}
                <button 
                  className="button is-info"
                  onClick={() => setShowCsvModal(true)}
                >
                  <span className="icon is-small">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="17 8 12 3 7 8"/>
                      <line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                  </span>
                  <span>CSV Upload</span>
                </button>
                <button 
                  className="button"
                  onClick={handleCreateContact}
                >
                  Create New Contact
                </button>
              </div>
            </div>

            {successMessage && (
              <div className="notification is-success">
                <button 
                  className="delete" 
                  onClick={() => setSuccessMessage('')}
                ></button>
                {successMessage}
              </div>
            )}

            {error && (
              <div className="notification is-danger">
                <button 
                  className="delete" 
                  onClick={() => setError('')}
                ></button>
                {error}
                <br />
                <button 
                  className="button is-small is-light mt-2" 
                  onClick={fetchContacts}
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Search and Filter Section */}
            <div className="box mb-4">
              <div className="columns">
                <div className="column is-5">
                  <div className="field">
                    <label className="pt-0 label">Search</label>
                    <div className="control has-icons-left">
                      <input
                        className="input"
                        type="text"
                        placeholder="Search by name, email, or phone..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <span className="icon is-small is-left">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <circle cx="11" cy="11" r="8"/>
                          <path d="m21 21-4.35-4.35"/>
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="column is-3">
                  <div className="field">
                    <label className="pt-0 label">Filter by Role</label>
                    <div className="control">
                      <div className="select is-fullwidth">
                        <select
                          value={selectedRole}
                          onChange={(e) => setSelectedRole(e.target.value)}
                        >
                          <option value="">All Roles</option>
                          <option value="participant">Participant</option>
                          <option value="host">Host</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="column is-3">
                  <div className="field">
                    <label className="pt-0 label">Filter by Status</label>
                    <div className="control">
                      <div className="select is-fullwidth">
                        <select
                          value={selectedStatus}
                          onChange={(e) => setSelectedStatus(e.target.value)}
                        >
                          <option value="">All Statuses</option>
                          <option value="active">Active</option>
                          <option value="archived">Archived</option>
                          <option value="prospecting">Prospecting</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="column is-1">
                  <div className="field">
                    <label className="pt-0 label">&nbsp;</label>
                    <div className="control">
                      <button 
                        className="button is-light is-fullwidth"
                        onClick={clearFilters}
                        disabled={!searchTerm && !selectedRole && !selectedStatus}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Archived contacts warning */}
            {selectedStatus === 'archived' && selectedContacts.length > 0 && (
              <div className="notification is-warning mb-4">
                <p>
                  <span className='colorBlack is-bold'>These contacts are archived.</span> You cannot create pods with archived contacts. 
                  To add them to a pod, please restore the contacts first by changing their status.
                </p>
              </div>
            )}

            {/* Warning: Contacts already in active pods */}
            {contactsAlreadyInPod.length > 0 && (
              <div className="notification is-danger is-light mb-4">
                <p>
                  <span className='colorBlack is-bold'>⚠️ {contactsAlreadyInPod.length}</span> selected contact{contactsAlreadyInPod.length !== 1 ? 's are' : ' is'} already in an active pod and cannot be added to a new pod:
                </p>
                <ul className="mt-2" style={{ marginLeft: '1.5rem' }}>
                  {contactsAlreadyInPodObjects.slice(0, 5).map(contact => (
                    <li key={contact.id || contact._id}>
                      <strong>{contact.fullName}</strong> → {contact.activePod?.podName}
                    </li>
                  ))}
                  {contactsAlreadyInPodObjects.length > 5 && (
                    <li><em>...and {contactsAlreadyInPodObjects.length - 5} more</em></li>
                  )}
                </ul>
                <p className="mt-2 is-size-7">
                  Please deselect these contacts or remove them from their current pod first.
                </p>
              </div>
            )}

            {/* Pod eligibility info */}
            {selectedContacts.length > 0 && selectedContacts.length !== eligibleSelectedContacts.length && selectedStatus !== 'archived' && contactsAlreadyInPod.length === 0 && (
              <div className="notification is-info is-light mb-4">
                <p>
                  <span className='colorBlack is-bold'>{eligibleSelectedContacts.length}</span> of {selectedContacts.length} selected contact{selectedContacts.length !== 1 ? 's are' : ' is'} eligible for pods. 
                  Only participants who have opted in to SMS can be added to pods.
                  {eligibleForOptInContacts.length > 0 && (
                    <span className="ml-2">
                      <span className="tag is-info">{eligibleForOptInContacts.length} can receive opt-in requests</span>
                    </span>
                  )}
                </p>
              </div>
            )}

            {filteredAndSortedContacts.length === 0 && !loading && !error ? (
              <div className="notification is-info is-light">
                <p>
                  {searchTerm || selectedRole || selectedStatus
                    ? 'No contacts match your search criteria.'
                    : 'No contacts found.'}
                </p>
              </div>
            ) : (
              <>
                <div className="mb-4 is-flex is-justify-content-space-between is-align-items-center">
                  <p className="is-size-7">
                    Showing {filteredAndSortedContacts.length} of {contacts.length} contact{contacts.length !== 1 ? 's' : ''}
                    {' • '}
                    <span className="has-text-success has-text-weight-semibold">
                      {eligibleContacts.length} eligible for pods
                    </span>
                  </p>
                  {(searchTerm || selectedRole || selectedStatus) && (
                    <div className="tags">
                      {searchTerm && (
                        <span className="tag is-info">
                          Search: "{searchTerm}"
                          <button 
                            className="delete is-small" 
                            onClick={() => setSearchTerm('')}
                          ></button>
                        </span>
                      )}
                      {selectedRole && (
                        <span className="tag is-info">
                          Role: {selectedRole.charAt(0).toUpperCase() + selectedRole.slice(1)}
                          <button 
                            className="delete is-small" 
                            onClick={() => setSelectedRole('')}
                          ></button>
                        </span>
                      )}
                      {selectedStatus && (
                        <span className="tag is-info">
                          Status: {selectedStatus.charAt(0).toUpperCase() + selectedStatus.slice(1)}
                          <button 
                            className="delete is-small" 
                            onClick={() => setSelectedStatus('')}
                          ></button>
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="table-container">
                  <table className="table is-fullwidth is-hoverable">
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }}>
                          <input
                            type="checkbox"
                            checked={selectedContacts.length === filteredAndSortedContacts.length && filteredAndSortedContacts.length > 0}
                            onChange={handleSelectAll}
                          />
                        </th>
                        <th 
                          onClick={() => handleSort('fullName')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'fullName' ? 'colorBlack' : 'colorBlue'}
                        >
                          Name {getSortIcon('fullName')}
                        </th>
                        <th 
                          onClick={() => handleSort('contactInfo')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'contactInfo' ? 'colorBlack' : 'colorBlue'}
                        >
                          Contact Info {getSortIcon('contactInfo')}
                        </th>
                        <th 
                          onClick={() => handleSort('smsVerified')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'smsVerified' ? 'colorBlack' : 'colorBlue'}
                        >
                          Opt-In {getSortIcon('smsVerified')}
                        </th>
                        <th 
                          onClick={() => handleSort('role')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'role' ? 'colorBlack' : 'colorBlue'}
                        >
                          Role {getSortIcon('role')}
                        </th>
                        <th 
                          onClick={() => handleSort('status')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'status' ? 'colorBlack' : 'colorBlue'}
                        >
                          Status {getSortIcon('status')}
                        </th>
                        <th 
                          onClick={() => handleSort('createdAt')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'createdAt' ? 'colorBlack' : 'colorBlue'}
                        >
                          Created {getSortIcon('createdAt')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAndSortedContacts.map((contact) => {
                        const id = contact.id || contact._id;
                        const isSelected = selectedContacts.includes(id);
                        const eligible = isEligibleForPod(contact);
                        const canOptIn = isEligibleForOptIn(contact);
                        const ineligibleReason = getIneligibleReason(contact);
                        
                        const hasPhone = !!contact.phone;
                        const hasEmail = !!contact.email;
                        
                        return (
                          <tr 
                            key={id}
                            onClick={(e) => handleRowClick(id, e)}
                            style={{
                              cursor: 'pointer',
                              transition: 'transform 0.1s ease',
                              backgroundColor: isSelected ? '#f0f8ff' : 'transparent',
                              opacity: eligible ? 1 : 0.7
                            }}
                            title={eligible ? '' : `Not eligible for pods: ${ineligibleReason}`}
                            onMouseEnter={(e) => {
                              if (!isSelected) e.currentTarget.style.backgroundColor = '#fafafa';
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <td onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleSelectContact(id)}
                              />
                            </td>
                            <td>
                              {contact.fullName}
                              {contact.activePod && (
                                <span className="ml-2 tag is-danger is-light is-small" title={`In pod: ${contact.activePod.podName}`}>
                                  In Pod
                                </span>
                              )}
                              {!eligible && contact.role === 'participant' && !contact.activePod && (
                                <span className="ml-2 tag is-warning is-light is-small" title="Can receive opt-in request">
                                  {canOptIn ? 'Needs opt-in' : 'Not opted in'}
                                </span>
                              )}
                              {contact.role !== 'participant' && (
                                <span className="ml-2 tag is-light is-small">
                                  Not a participant
                                </span>
                              )}
                            </td>
                            <td>
                              {hasPhone && (
                                <span>{contact.phone}</span>
                              )}
                              {hasPhone && hasEmail && (
                                <br />
                              )}
                              {hasEmail && (
                                <span className={hasPhone ? 'is-size-7 has-text-grey' : ''}>
                                  {contact.email}
                                  {contact.hasCorruptedEmail && (
                                    <span className="tag is-small is-danger ml-2">Corrupted</span>
                                  )}
                                </span>
                              )}
                              {!hasPhone && !hasEmail && (
                                <span className="has-text-grey-light">Not provided</span>
                              )}
                            </td>
                            <td>
                              {contact.smsVerified ? (
                                <span className="tag is-small is-success">Yes</span>
                              ) : (
                                <span className="tag is-small is-warning">No</span>
                              )}
                            </td>
                            <td>
                              <span className={`tag ${getRoleColor(contact.role)}`}>
                                {contact.role.charAt(0).toUpperCase() + contact.role.slice(1)}
                              </span>
                            </td>
                            <td>
                              <span className={`tag ${getStatusColor(contact.status)}`}>
                                {contact.status.charAt(0).toUpperCase() + contact.status.slice(1)}
                              </span>
                            </td>
                            <td>{formatDate(contact.createdAt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}

          </div>

        </div>
      </section>

      {/* Create Pod Modal */}
      {showPodModal && (
        <div className="modal is-active">
          <div className="modal-background" onClick={() => setShowPodModal(false)}></div>
          <div className="modal-card">
            <header className="modal-card-head">
              <h1 className="modal-card-title is-none is-bold colorBlack">Create New Pod</h1>
              <button 
                className="delete" 
                aria-label="close"
                onClick={() => setShowPodModal(false)}
              ></button>
            </header>
            <section className="my-0 py-0 modal-card-body">
              <div className="field">
                <label className="my-0 py-0 label">Pod ID</label>
                <div className="control">
                  <input
                    className="input"
                    type="text"
                    placeholder="POD-12345"
                    value={podId}
                    onChange={(e) => setPodId(e.target.value)}
                  />
                </div>
                <p className="mx-3 mt-1 is-size-7">Unique identifier for this pod</p>
              </div>

              <div className="field">
                <label className="label">Pod Name</label>
                <div className="control">
                  <input
                    className="input"
                    type="text"
                    placeholder="Enter pod name..."
                    value={podName}
                    onChange={(e) => setPodName(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>

              <div className="notification backgroundColorGreen">
                <p><span className='colorBlack is-bold'>{eligibleSelectedContacts.length}</span> eligible contact{eligibleSelectedContacts.length !== 1 ? 's' : ''} will be added to this pod</p>
                {selectedContacts.length !== eligibleSelectedContacts.length && (
                  <p className="is-size-7 mt-2">
                    <em>{selectedContacts.length - eligibleSelectedContacts.length} ineligible contact{selectedContacts.length - eligibleSelectedContacts.length !== 1 ? 's' : ''} will be skipped (not participants or not opted in)</em>
                  </p>
                )}
              </div>
            </section>
            <footer className="modal-card-foot">
              <button 
                className={`mr-4 button is-success ${creatingPod ? 'is-loading' : ''}`}
                onClick={handleCreatePod}
                disabled={!podName.trim() || !podId.trim() || creatingPod || eligibleSelectedContacts.length === 0}
              >
                Create Pod
              </button>
              <button 
                className="button" 
                onClick={() => setShowPodModal(false)}
                disabled={creatingPod}
              >
                Cancel
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* Change Status Modal */}
      {showStatusModal && (
        <div className="modal is-active">
          <div className="modal-background" onClick={() => setShowStatusModal(false)}></div>
          <div className="modal-card">
            <header className="modal-card-head">
              <h1 className="modal-card-title is-none is-bold colorBlack">
                Change Status{selectedContacts.length > 1 ? 'es' : ''}
              </h1>
              <button 
                className="delete" 
                aria-label="close"
                onClick={() => setShowStatusModal(false)}
              ></button>
            </header>
            <section className="my-0 py-0 modal-card-body">
              <div className="content">
                <p>Select the new status for the selected contact{selectedContacts.length > 1 ? 's' : ''}:</p>
              </div>

              <div className="field">
                <label className="label">New Status</label>
                <div className="control">
                  <div className="select is-fullwidth">
                    <select
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                    >
                      <option value="">Select a status...</option>
                      <option value="active">Active</option>
                      <option value="archived">Archived</option>
                      <option value="prospecting">Prospecting</option>
                    </select>
                  </div>
                </div>
                <p className="help">
                  {newStatus === 'active' && 'Active contacts appear in the default contact list and can participate in programs.'}
                  {newStatus === 'archived' && 'Archived contacts are hidden from the default list but can be restored at any time.'}
                  {newStatus === 'prospecting' && 'Prospecting contacts are potential participants being considered for future programs.'}
                </p>
              </div>

              <div className={`notification ${newStatus ? getStatusColor(newStatus) : 'is-light'} is-light mt-4`}>
                <p>
                  <span className='colorBlack is-bold'>{selectedContacts.length}</span> contact{selectedContacts.length !== 1 ? 's' : ''} will be updated
                  {newStatus && (
                    <> to <span className={`tag ${getStatusColor(newStatus)}`}>{newStatus.charAt(0).toUpperCase() + newStatus.slice(1)}</span></>
                  )}
                </p>
              </div>
            </section>
            <footer className="modal-card-foot">
              <button 
                className={`mr-4 button is-primary ${updatingStatus ? 'is-loading' : ''}`}
                onClick={handleUpdateStatus}
                disabled={!newStatus || updatingStatus}
              >
                Update Status{selectedContacts.length > 1 ? 'es' : ''}
              </button>
              <button 
                className="button" 
                onClick={() => setShowStatusModal(false)}
                disabled={updatingStatus}
              >
                Cancel
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* Bulk Opt-In Modal */}
      {showBulkOptInModal && (
        <div className="modal is-active">
          <div className="modal-background" onClick={() => !sendingBulkOptIn && resetBulkOptInModal()}></div>
          <div className="modal-card" style={{ maxWidth: '700px', width: '90%' }}>
            <header className="modal-card-head">
              <h1 className="modal-card-title is-none is-bold colorBlack">
                📱 Send Bulk Opt-In Requests
              </h1>
              <button 
                className="delete" 
                aria-label="close"
                onClick={resetBulkOptInModal}
                disabled={sendingBulkOptIn}
              ></button>
            </header>
            <section className="modal-card-body">
              {/* Results display */}
              {bulkOptInResults && (
                <div className="notification is-success mb-4">
                  <p className="has-text-weight-bold">Opt-in requests sent.</p>
                  <p className="is-size-7 mt-1">
                    Sent: {bulkOptInResults.sent || 0}
                    {bulkOptInResults.failed > 0 && <span> • Failed: {bulkOptInResults.failed}</span>}
                    {bulkOptInResults.skipped > 0 && <span> • Skipped: {bulkOptInResults.skipped}</span>}
                  </p>
                </div>
              )}

              {/* Contacts to receive opt-in */}
              <div className="notification is-info is-light mb-4">
                <p>
                  <span className="has-text-weight-bold">{eligibleForOptInContacts.length}</span> contact{eligibleForOptInContacts.length !== 1 ? 's' : ''} will receive an opt-in request:
                </p>
                <div className="mt-2" style={{ maxHeight: '120px', overflowY: 'auto' }}>
                  {eligibleForOptInContactObjects.slice(0, 10).map((contact, index) => (
                    <span key={contact.id || contact._id} className="tag is-light mr-1 mb-1">
                      {contact.fullName}
                    </span>
                  ))}
                  {eligibleForOptInContactObjects.length > 10 && (
                    <span className="tag is-warning is-light">
                      +{eligibleForOptInContactObjects.length - 10} more
                    </span>
                  )}
                </div>
              </div>

              {/* Opt-in message */}
              <div className="field">
                <label className="label">
                  Opt-In Request Message
                  <span className="has-text-weight-normal has-text-grey ml-2">(sent to each contact)</span>
                </label>
                <div className="control">
                  <textarea
                    className="textarea"
                    value={bulkOptInMessage}
                    onChange={(e) => setBulkOptInMessage(e.target.value)}
                    rows="5"
                    placeholder="Enter the message that will be sent to contacts asking them to opt-in..."
                    disabled={sendingBulkOptIn}
                  />
                </div>
                <p className="help">
                  {bulkOptInMessage.length} characters
                  {bulkOptInMessage.length > 160 && (
                    <span className="has-text-warning ml-2">
                      (may be sent as {Math.ceil(bulkOptInMessage.length / 160)} SMS segments)
                    </span>
                  )}
                </p>
              </div>

              {/* Advanced options toggle */}
              <div className="field mt-4">
                <button
                  type="button"
                  className="button is-small is-light"
                  onClick={() => setShowAdvancedOptInOptions(!showAdvancedOptInOptions)}
                  disabled={sendingBulkOptIn}
                >
                  {showAdvancedOptInOptions ? '▼' : '▶'} Customize Auto-Response Messages
                </button>
              </div>

              {/* Advanced options */}
              {showAdvancedOptInOptions && (
                <div className="box mt-3" style={{ backgroundColor: '#f9f9f9' }}>
                  <p className="is-size-7 has-text-grey mb-3">
                    These messages are automatically sent when a contact replies with the appropriate keyword.
                  </p>

                  {/* Opt-in confirmation */}
                  <div className="field">
                    <label className="label is-small">
                      Opt-In Confirmation
                      <span className="has-text-weight-normal has-text-grey ml-2">(sent when user replies JOIN/START)</span>
                    </label>
                    <div className="control">
                      <textarea
                        className="textarea is-small"
                        value={bulkOptInConfirmation}
                        onChange={(e) => setBulkOptInConfirmation(e.target.value)}
                        rows="3"
                        disabled={sendingBulkOptIn}
                      />
                    </div>
                    <p className="help is-size-7">
                      {bulkOptInConfirmation.length} characters
                      {bulkOptInConfirmation === DEFAULT_OPT_IN_CONFIRMATION && ' (default)'}
                    </p>
                  </div>

                  {/* Opt-out confirmation */}
                  <div className="field mt-3">
                    <label className="label is-small">
                      Opt-Out Confirmation
                      <span className="has-text-weight-normal has-text-grey ml-2">(sent when user replies STOP/QUIT)</span>
                    </label>
                    <div className="control">
                      <textarea
                        className="textarea is-small"
                        value={bulkOptOutConfirmation}
                        onChange={(e) => setBulkOptOutConfirmation(e.target.value)}
                        rows="3"
                        disabled={sendingBulkOptIn}
                      />
                    </div>
                    <p className="help is-size-7">
                      {bulkOptOutConfirmation.length} characters
                      {bulkOptOutConfirmation === DEFAULT_OPT_OUT_CONFIRMATION && ' (default)'}
                    </p>
                  </div>

                  {/* Reset to defaults button */}
                  <div className="field mt-3">
                    <button
                      type="button"
                      className="button is-small is-text"
                      onClick={() => {
                        setBulkOptInConfirmation(DEFAULT_OPT_IN_CONFIRMATION(companyName));
                        setBulkOptOutConfirmation(DEFAULT_OPT_OUT_CONFIRMATION(companyName));
                      }}
                      disabled={sendingBulkOptIn}
                    >
                      Reset to Defaults
                    </button>
                  </div>
                </div>
              )}

              {/* Warning about SMS costs */}
              <div className="notification is-warning is-light mt-4">
                <p className="is-size-7">
                  <span className="has-text-weight-bold">Note:</span> This will send {eligibleForOptInContacts.length} SMS message{eligibleForOptInContacts.length !== 1 ? 's' : ''}. 
                  Standard messaging rates apply. Contacts who have already opted out at the carrier level may not receive messages.
                </p>
              </div>
            </section>
            <footer className="modal-card-foot">
              <button 
                className={`mr-4 button is-success ${sendingBulkOptIn ? 'is-loading' : ''}`}
                onClick={handleSendBulkOptIn}
                disabled={sendingBulkOptIn || !bulkOptInMessage.trim() || eligibleForOptInContacts.length === 0 || bulkOptInResults}
              >
                {bulkOptInResults ? 'Sent' : `Send to ${eligibleForOptInContacts.length} Contact${eligibleForOptInContacts.length !== 1 ? 's' : ''}`}
              </button>
              <button 
                className="button" 
                onClick={resetBulkOptInModal}
                disabled={sendingBulkOptIn}
              >
                {bulkOptInResults ? 'Close' : 'Cancel'}
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* CSV Upload Modal */}
      {showCsvModal && (
        <div className="modal is-active">
          <div className="modal-background" onClick={resetCsvModal}></div>
          <div className="modal-card" style={{ maxWidth: '800px', width: '90%' }}>
            <header className="modal-card-head">
              <h1 className="modal-card-title is-none is-bold colorBlack">
                CSV Upload - {csvStep === 1 ? 'Select File' : csvStep === 2 ? 'Map Fields' : 'Preview & Confirm'}
              </h1>
              <button 
                className="delete" 
                aria-label="close"
                onClick={resetCsvModal}
              ></button>
            </header>
            
            <section className="modal-card-body">
              {/* Error Display */}
              {csvErrors.length > 0 && (
                <div className="notification is-danger is-light mb-4">
                  <button className="delete" onClick={() => setCsvErrors([])}></button>
                  <ul style={{ marginLeft: '1rem' }}>
                    {csvErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Step 1: File Upload */}
              {csvStep === 1 && (
                <div>
                  <div className="content">
                    <p>Upload a CSV file containing your contacts. The file should include headers in the first row.</p>
                  </div>

                  {/* Role Selection */}
                  <div className="field mb-4">
                    <label className="label">What type of contacts are you importing?</label>
                    <div className="control">
                      <div className="select is-fullwidth">
                        <select
                          value={csvImportRole}
                          onChange={(e) => setCsvImportRole(e.target.value)}
                        >
                          <option value="participant">Participants (Name + Phone required)</option>
                          <option value="host">Hosts (Name + Email required)</option>
                          <option value="admin">Admins (Name + Email required)</option>
                        </select>
                      </div>
                    </div>
                    <p className="help mt-2">
                      {csvImportRole === 'participant' ? (
                        <><span className='colorBlack is-bold'>Required:</span> Full Name, Phone &nbsp;|&nbsp; <span className='colorBlack is-bold'>Optional:</span> Email, Status</>
                      ) : (
                        <><span className='colorBlack is-bold'>Required:</span> Full Name, Email &nbsp;|&nbsp; <span className='colorBlack is-bold'>Optional:</span> Phone, Status</>
                      )}
                    </p>
                  </div>
                  
                  <div className="file is-boxed is-centered is-info has-name">
                    <label className="file-label">
                      <input 
                        className="file-input" 
                        type="file" 
                        accept=".csv"
                        onChange={handleCsvFileChange}
                      />
                      <span className="file-cta">
                        <span className="file-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="17 8 12 3 7 8"/>
                            <line x1="12" y1="3" x2="12" y2="15"/>
                          </svg>
                        </span>
                        <span className="file-label">Choose a CSV file…</span>
                      </span>
                      {csvFile && (
                        <span className="file-name">{csvFile.name}</span>
                      )}
                    </label>
                  </div>

                  <div className="box mt-5" style={{ backgroundColor: '#f5f5f5' }}>
                    <p className="has-text-weight-bold mb-2">Example CSV Format for {csvImportRole === 'participant' ? 'Participants' : csvImportRole.charAt(0).toUpperCase() + csvImportRole.slice(1) + 's'}:</p>
                    <pre style={{ fontSize: '0.85rem', overflow: 'auto' }}>
{csvImportRole === 'participant' ? 
`Full Name,Phone,Email,Status
John Smith,+1234567890,john@example.com,active
Jane Doe,+0987654321,,prospecting` :
`Full Name,Email,Phone,Status
John Smith,john@example.com,+1234567890,active
Jane Doe,jane@example.com,,active`}
                    </pre>
                  </div>
                </div>
              )}

              {/* Step 2: Field Mapping */}
              {csvStep === 2 && (
                <div>
                  <div className="content mb-4">
                    <p>Map your CSV columns to contact fields. Required fields are marked with <span className="has-text-danger">*</span></p>
                    <p className="is-size-7 has-text-grey">
                      Found <span className='colorBlack is-bold'>{csvData.length}</span> row{csvData.length !== 1 ? 's' : ''} and <span className='colorBlack is-bold'>{csvHeaders.length}</span> column{csvHeaders.length !== 1 ? 's' : ''} in your CSV file.
                    </p>
                  </div>

                  {/* Role indicator */}
                  <div className="notification is-light mb-4">
                    <p className="is-size-7">
                      <span className='colorBlack is-bold'>Importing as:</span>{' '}
                      <span className={`tag ${csvImportRole === 'admin' ? 'is-danger' : csvImportRole === 'host' ? 'is-warning' : 'is-primary'}`}>
                        {csvImportRole.charAt(0).toUpperCase() + csvImportRole.slice(1)}
                      </span>
                      <button 
                        className="button is-small is-text ml-2"
                        onClick={() => setCsvStep(1)}
                      >
                        Change
                      </button>
                    </p>
                  </div>

                  <div className="table-container">
                    <table className="table is-fullwidth">
                      <thead>
                        <tr>
                          <th className="colorBlack" style={{ width: '40%' }}>Contact Field</th>
                          <th className="colorBlack" style={{ width: '60%' }}>CSV Column</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contactFields.map(field => (
                          <tr key={field.key}>
                            <td>
                              <span className='colorBlack is-bold'>{field.label}</span>
                              {field.required && <span className="has-text-danger ml-1">*</span>}
                            </td>
                            <td>
                              <div className="select is-fullwidth">
                                <select
                                  value={fieldMapping[field.key] || ''}
                                  onChange={(e) => handleMappingChange(field.key, e.target.value)}
                                >
                                  <option value="">-- Select Column --</option>
                                  {csvHeaders.map(header => (
                                    <option key={header} value={header}>{header}</option>
                                  ))}
                                </select>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="notification is-info is-light mt-4">
                    <p className="is-size-7">
                      <span className='colorBlack is-bold'>Tip:</span> If Status column is not mapped or has invalid values, 
                      contacts will default to "Active" status.
                    </p>
                  </div>
                </div>
              )}

              {/* Step 3: Preview & Confirm */}
              {csvStep === 3 && (
                <div>
                  <div className="content mb-4">
                    <p>Review the mapped data before importing. Showing first 5 rows as preview.</p>
                  </div>

                  {/* Role indicator */}
                  <div className="notification is-light mb-4">
                    <p className="is-size-7">
                      <span className='colorBlack is-bold'>Importing as:</span>{' '}
                      <span className={`tag ${csvImportRole === 'admin' ? 'is-danger' : csvImportRole === 'host' ? 'is-warning' : 'is-primary'}`}>
                        {csvImportRole.charAt(0).toUpperCase() + csvImportRole.slice(1)}
                      </span>
                    </p>
                  </div>

                  <div className="table-container">
                    <table className="table is-fullwidth is-striped is-narrow">
                      <thead>
                        <tr>
                          <th>#</th>
                          {contactFields.map(field => (
                            <th key={field.key}>
                              {field.label}
                              {field.required && <span className="has-text-danger ml-1">*</span>}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {getMappedPreviewData.map((row, index) => (
                          <tr key={index}>
                            <td>{row._rowIndex}</td>
                            {contactFields.map(field => (
                              <td key={field.key}>
                                {row[field.key] || <span className="has-text-grey-light">—</span>}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {csvData.length > 5 && (
                    <p className="is-size-7 has-text-grey mt-2">
                      ... and {csvData.length - 5} more row{csvData.length - 5 !== 1 ? 's' : ''}
                    </p>
                  )}

                  <div className="notification is-success is-light mt-4">
                    <p>
                      <span className='colorBlack is-bold'>{csvData.length}</span> {csvImportRole}{csvData.length !== 1 ? 's' : ''} will be created.
                    </p>
                    <p className="is-size-7 mt-2">
                      {csvImportRole === 'participant' 
                        ? 'Duplicate phone numbers will be skipped. Contacts missing Name or Phone will be skipped.'
                        : 'Duplicate emails will be skipped. Contacts missing Name or Email will be skipped.'
                      }
                    </p>
                  </div>
                </div>
              )}
            </section>

            <footer className="modal-card-foot is-justify-content-space-between">
              <div>
                {csvStep > 1 && (
                  <button 
                    className="button"
                    onClick={() => setCsvStep(csvStep - 1)}
                    disabled={uploadingCsv}
                  >
                    Back
                  </button>
                )}
              </div>
              <div className="buttons">
                <button 
                  className="button" 
                  onClick={resetCsvModal}
                  disabled={uploadingCsv}
                >
                  Cancel
                </button>
                
                {csvStep === 2 && (
                  <button 
                    className="button is-info"
                    onClick={handleProceedToPreview}
                  >
                    Next: Preview
                  </button>
                )}
                
                {csvStep === 3 && (
                  <button 
                    className={`button is-success ${uploadingCsv ? 'is-loading' : ''}`}
                    onClick={handleUploadCsv}
                    disabled={uploadingCsv}
                  >
                    Import {csvData.length} {csvImportRole.charAt(0).toUpperCase() + csvImportRole.slice(1)}{csvData.length !== 1 ? 's' : ''}
                  </button>
                )}
              </div>
            </footer>
          </div>
        </div>
      )}
    </>
  );
};

export default Contacts;